import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import {
  validateTemplate,
  ValidationResult,
} from "../Helpers/TemplateValidator";
import { UserTemplate } from "../storage/templateStorage";
import { fixTemplateWithAI } from "../services/templateFixService";
import "../styles/components/TemplateEditorDialog.css";

interface TemplateEditorDialogProps {
  visible: boolean;
  template?: UserTemplate;
  onClose: () => void;
  onSave: (markdown: string, templateId?: string) => Promise<void>;
}

const SCAFFOLD_TEMPLATE = `# My Template

Author: Your Name
Description: A brief description of what this template is for and how it should be used.
Image: https://example.com/image.png
NodeType: Card

\`\`\`yaml
rulesVersion: 1

# Define how to infer roles from node labels
roleInference:
  byLabelPrefix:
    - prefix: "Goal"
      role: goal
    - prefix: "Task"
      role: task
    - prefix: "Note"
      role: note

# Suggest what children to add based on parent role
suggestNextChild:
  - when:
      parentRole: goal
    suggest:
      - role: task
        labelTemplate: "Task {n}"
        reason: "Break the goal into actionable tasks."

  - when:
      parentRole: task
    suggest:
      - role: note
        labelTemplate: "Note {parent}.{n}"
        reason: "Add details or context to the task."
\`\`\`

- Goal 1
  - Notes: The main objective to achieve.
  - Task 1.1
    - Notes: First step towards the goal.
  - Task 1.2
    - Notes: Second step towards the goal.
`;

const TemplateEditorDialog: React.FC<TemplateEditorDialogProps> = ({
  visible,
  template,
  onClose,
  onSave,
}) => {
  const [markdown, setMarkdown] = useState<string>("");
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [saving, setSaving] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [fixing, setFixing] = useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const lineNumbersRef = React.useRef<HTMLDivElement>(null);

  // Helper to darken a color for borders
  const darkenColor = (hex: string): string => {
    const rgb = parseInt(hex.slice(1), 16);
    const r = Math.max(0, ((rgb >> 16) & 0xff) - 40);
    const g = Math.max(0, ((rgb >> 8) & 0xff) - 40);
    const b = Math.max(0, (rgb & 0xff) - 40);
    return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
  };

  // Get line numbers with errors and their colors
  const getErrorLines = (): Map<number, string> => {
    const errorLines = new Map<number, string>();
    if (validation) {
      validation.errors.forEach((error) => {
        if (error.line && error.color) {
          errorLines.set(error.line, error.color);
        }
      });
    }
    return errorLines;
  };

  // Synchronize scroll between textarea and line numbers
  const handleScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
    if (lineNumbersRef.current && textareaRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  useEffect(() => {
    if (visible) {
      if (template) {
        setMarkdown(template.markdown);
      } else {
        setMarkdown("");
      }
      setValidation(null);
    }
  }, [visible, template]);

  const handleLoadScaffold = () => {
    setMarkdown(SCAFFOLD_TEMPLATE);
    setValidation(null);
  };

  const handleValidate = () => {
    const result = validateTemplate(markdown);
    setValidation(result);
  };

  const handleFix = async () => {
    setFixing(true);
    setValidation(null);
    try {
      const fixedTemplate = await fixTemplateWithAI(markdown);
      setMarkdown(fixedTemplate);

      // Validate the fixed template
      const result = validateTemplate(fixedTemplate);
      setValidation(result);

      if (result.isValid) {
        // Show success message briefly
        setTimeout(() => setValidation(null), 3000);
      }
    } catch (error) {
      console.error("Error fixing template:", error);
      alert(
        `Error fixing template: ${error instanceof Error ? error.message : String(error)}`,
      );
    } finally {
      setFixing(false);
    }
  };

  const handleSave = async () => {
    const result = validateTemplate(markdown);
    setValidation(result);

    if (!result.isValid) {
      return;
    }

    setSaving(true);
    try {
      await onSave(markdown, template?.templateId);
      onClose();
    } catch (error) {
      console.error("Error saving template:", error);
      alert(
        `Error saving template: ${error instanceof Error ? error.message : String(error)}`,
      );
    } finally {
      setSaving(false);
    }
  };

  const renderValidationMessages = () => {
    if (!validation) return null;

    return (
      <div className="validation-messages">
        {validation.errors.map((error, index) => (
          <div
            key={`error-${index}`}
            className="validation-message-item"
            style={{
              backgroundColor: error.color || "#ffebee",
              padding: "0.75rem",
              borderRadius: "4px",
              borderLeft: `4px solid ${darkenColor(error.color || "#ffebee")}`,
              marginBottom: "0.5rem",
            }}
          >
            <div style={{ fontWeight: "bold", marginBottom: "0.25rem" }}>
              {error.line ? `Line ${error.line}: ` : ""}Error
            </div>
            <div style={{ whiteSpace: "pre-wrap", lineHeight: "1.5" }}>
              {error.message}
            </div>
          </div>
        ))}
        {validation.warnings.map((warning, index) => (
          <Message
            key={`warning-${index}`}
            severity="warn"
            text={warning.message}
            className="mb-2"
          />
        ))}
        {validation.isValid && validation.warnings.length === 0 && (
          <Message
            severity="success"
            text="Template is valid! ✓"
            className="mb-2"
          />
        )}
      </div>
    );
  };

  const renderHelpContent = () => {
    return (
      <div className="template-help-content">
        <h3>Template Authoring Guide</h3>

        <h4>Structure</h4>
        <p>A template consists of:</p>
        <ul>
          <li>
            <strong>Title:</strong> H1 heading (e.g., <code># My Template</code>
            )
          </li>
          <li>
            <strong>Metadata:</strong> Optional fields like Author, Image,
            NodeType
          </li>
          <li>
            <strong>Rules:</strong> YAML block defining role inference and
            suggestions
          </li>
          <li>
            <strong>Tree:</strong> Hierarchical list of nodes with optional
            notes
          </li>
        </ul>

        <h4>Metadata Fields</h4>
        <ul>
          <li>
            <code>Author: Your Name</code> - Shows as subtitle
          </li>
          <li>
            <code>Image: https://...</code> - Template card image
          </li>
          <li>
            <code>NodeType: Card|Note|Center|Image|MindMap</code> - Default node
            type
          </li>
        </ul>

        <h4>YAML Rules</h4>
        <p>Define role inference and child suggestions:</p>
        <pre>{`roleInference:
  byLabelPrefix:
    - prefix: "Goal"
      role: goal

suggestNextChild:
  - when:
      parentRole: goal
    suggest:
      - role: task
        labelTemplate: "Task {n}"
        reason: "Add tasks"`}</pre>

        <h4>Tree Structure</h4>
        <p>Use markdown lists with optional notes:</p>
        <pre>{`- Parent Node
  - Notes: Description here
  - Child Node 1
    - Notes: Details
  - Child Node 2`}</pre>
      </div>
    );
  };

  const footer = (
    <div className="template-editor-footer">
      <Button
        label="Load Scaffold"
        icon="pi pi-file"
        onClick={handleLoadScaffold}
        className="p-button-outlined"
        disabled={saving || fixing}
      />
      <Button
        label="Help"
        icon="pi pi-question-circle"
        onClick={() => setShowHelp(true)}
        className="p-button-outlined"
        disabled={saving || fixing}
      />
      <Button
        label="Validate"
        icon="pi pi-check-circle"
        onClick={handleValidate}
        className="p-button-outlined"
        disabled={saving || fixing}
      />
      <Button
        label="Fix"
        icon="pi pi-sparkles"
        onClick={handleFix}
        className="p-button-outlined"
        loading={fixing}
        disabled={saving || !markdown.trim()}
        tooltip="Use AI to fix template errors"
        tooltipOptions={{ position: "top" }}
      />
      <div style={{ flex: 1 }} />
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={onClose}
        className="p-button-text"
        disabled={saving || fixing}
      />
      <Button
        label="Save"
        icon="pi pi-save"
        onClick={handleSave}
        loading={saving}
        disabled={!markdown.trim() || fixing}
      />
    </div>
  );

  return (
    <>
      <Dialog
        visible={visible}
        onHide={onClose}
        header={template ? "Edit Template" : "Create Template"}
        footer={footer}
        className="template-editor-dialog"
        style={{ width: "80vw", maxWidth: "1200px" }}
        maximizable
      >
        <div className="template-editor-content">
          {renderValidationMessages()}

          <div className="template-editor-wrapper">
            <div className="line-numbers-overlay" ref={lineNumbersRef}>
              {markdown.split("\n").map((line, index) => {
                const lineNumber = index + 1;
                const errorLines = getErrorLines();
                const bgColor = errorLines.get(lineNumber);
                return (
                  <div
                    key={index}
                    className="line-number"
                    style={{
                      backgroundColor: bgColor || "transparent",
                      borderLeft: bgColor
                        ? `3px solid ${darkenColor(bgColor)}`
                        : "none",
                    }}
                  >
                    {lineNumber}
                  </div>
                );
              })}
            </div>
            <textarea
              ref={textareaRef}
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
              onScroll={handleScroll}
              className="template-editor-textarea"
              placeholder="Enter your template markdown here..."
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={showHelp}
        onHide={() => setShowHelp(false)}
        header="Template Authoring Help"
        style={{ width: "600px" }}
        dismissableMask
      >
        {renderHelpContent()}
      </Dialog>
    </>
  );
};

export default TemplateEditorDialog;
